import { Camera, User, Shield, ExternalLink } from 'lucide-react';
import { Avatar } from './ui/Avatar';
import { User as FirebaseUser } from 'firebase/auth';
import { useRef, useEffect, useState } from 'react';

interface BrandingInfo {
  brandName?: string;
  brandTagline?: string;
}

interface UserProfile {
  displayName: string;
  photoUrl?: string | null;
}

interface HeaderProps {
  onReportClick: () => void;
  user: FirebaseUser | null;
  userProfile: UserProfile | null;
  onProfileClick: () => void;
  branding?: BrandingInfo;
}

export const Header = ({
  onReportClick,
  user,
  userProfile,
  onProfileClick,
  branding,
}: HeaderProps) => {
  // Prioritize user.photoURL (from Google sign-in) over userProfile.photoUrl (from Firestore)
  // This ensures Google photos show immediately without waiting for Firestore sync
  const displayName = userProfile?.displayName || user?.displayName || 'Guest';
  const photoUrl = user?.photoURL || userProfile?.photoUrl || undefined;
  const isAuthenticated = !!(userProfile || user);
  const effectiveBrandName = branding?.brandName || 'VerifyPH';
  const effectiveTagline = branding?.brandTagline || 'Transparency by the People, for the People';
  
  // Measure banner height to position header correctly
  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(73); // Default fallback
  
  useEffect(() => {
    const updateBannerHeight = () => {
      if (bannerRef.current) {
        setBannerHeight(bannerRef.current.offsetHeight);
      }
    };
    
    updateBannerHeight();
    window.addEventListener('resize', updateBannerHeight);
    
    return () => window.removeEventListener('resize', updateBannerHeight);
  }, []);
  
  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };
  
  return (
    <>
      {/* DILG Anti-Epal Drive Banner - Less prominent */}
      <div 
        ref={bannerRef}
        className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2 px-4 sticky top-0 z-50 shadow-sm"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2 text-xs">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Shield size={14} className="flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-blue-50 text-xs leading-tight truncate">
                Supporting DILG Memorandum NC-2026-1047: Document evidence-based observations of government-funded projects
              </p>
            </div>
          </div>
          <a 
            href="https://dilg.gov.ph/news/ANTI-EPAL-DRIVE-DILG-Orders-Immediate-Removal-of-Officials-Names-and-Images-from-Government-Projects/NC-2026-1047"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs underline hover:text-blue-100 flex items-center gap-1 flex-shrink-0 whitespace-nowrap"
          >
            View Memorandum
            <ExternalLink size={12} />
          </a>
        </div>
      </div>

      <header 
        className="bg-blue-800 text-white shadow-lg sticky z-40 backdrop-blur-sm bg-opacity-95"
        style={{ top: `${bannerHeight}px` }}
        role="banner"
      >
        {/* Skip to main content link for accessibility */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-white focus:text-blue-800 focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg focus:font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Skip to main content
        </a>
        
        <nav 
          className="max-w-3xl mx-auto px-4 py-3"
          aria-label="Main navigation"
        >
        <div className="flex justify-between items-center">
          {/* Logo/Brand Section */}
          <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="flex items-center gap-2 hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-blue-800 rounded-lg p-1 -m-1"
              aria-label="VerifyPH - Go to top"
            >
              <div 
                className="bg-blue-600 p-1.5 rounded-lg flex-shrink-0"
                aria-hidden="true"
              >
                <Shield size={20} className="text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="font-bold text-lg leading-tight truncate">
                  {effectiveBrandName}
                </h1>
                <p className="text-xs text-blue-200 truncate">
                  {effectiveTagline}
                </p>
              </div>
            </a>
          </div>

          {/* Action Buttons Section */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Report Button */}
            <button
              onClick={onReportClick}
              onKeyDown={(e) => handleKeyDown(e, onReportClick)}
              className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-3 sm:px-4 py-2 rounded-full text-sm font-semibold shadow-md transition-all duration-200 flex items-center gap-2 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={isAuthenticated ? 'Submit a new report' : 'Sign in to submit a report'}
              title={isAuthenticated ? 'Submit a new report' : 'Please sign in to submit a report'}
            >
              <Camera size={16} className="flex-shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">Report</span>
            </button>

            {/* Profile Button */}
            <button
              onClick={onProfileClick}
              onKeyDown={(e) => handleKeyDown(e, onProfileClick)}
              className="flex items-center gap-2 hover:bg-blue-700 active:bg-blue-600 pl-2 pr-1 py-1 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-blue-800 min-w-0"
              aria-label={isAuthenticated ? `Open ${displayName}'s profile` : 'Open profile menu'}
              aria-expanded="false"
            >
              {isAuthenticated ? (
                <>
                  <span className="text-sm font-medium hidden sm:block truncate max-w-[120px]">
                    {displayName}
                  </span>
                  <Avatar
                    name={displayName}
                    url={photoUrl}
                    size="sm"
                  />
                </>
              ) : (
                <>
                  <span className="text-sm font-medium hidden sm:block">
                    Guest
                  </span>
                  <div 
                    className="bg-blue-700 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    aria-hidden="true"
                  >
                    <User size={16} />
                  </div>
                </>
              )}
            </button>
          </div>
        </div>
      </nav>
    </header>
    </>
  );
};
